import React, { useState } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import {
  Col,
  Row,
  Form as BForm,
  Button,
  Tabs,
  Tab,
} from "@themesberg/react-bootstrap";
import { createCmsSchema } from "../schema";
import useCreateCms from "../hooks/useCreateCms";
import ClientFilter from "../../../../components/ClientFilter";
import PortalFilter from "../../../../components/PortalFilter";
import useCheckPermission from "../../../../utils/checkPermission";
import Trigger from "../../../../components/OverlayTrigger";
import { faImages } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GalleryModal } from "../../../../components/ConfirmationModal";
import EditCMSTemplate from "../../../../components/EditCMSTemplate";
import { languageCode } from "../../../../pages/Tenant/TenantConfigurations/Languages/constants";
import NotificationIconContainer from "../../../../components/NotificationIcon/NotificationIconContainer";
import Preloader from "../../../../components/Preloader";
import PageBackButton from '../../../../components/PageBackButton'
import {categoryLinkOptions} from "../constants"



const CreateCmsSkeleton = () => {
  const skeletonStyle = (width, height = "18px") => ({
    width: width || "120px",
    height: height,
    backgroundColor: "#e9ecef",
    borderRadius: "4px",
    display: "inline-block",
    animation: "pulse 1.5s infinite ease-in-out",
  });

  return (
    <div className="FormsBg">
      <Row className="mb-3 align-items-center">
        <Col xs={6}>
          <Col>
            <div
              className="skeleton-loader"
              style={skeletonStyle("80px")}
            ></div>
          </Col>
          <Col className="mt-2">
            <div
              className="skeleton-loader"
              style={skeletonStyle("100%", "38px")}
            ></div>
          </Col>
        </Col>

        <Col>
          <div className="skeleton-loader" style={skeletonStyle("80px")}></div>
          <div
            className="mt-2 skeleton-loader"
            style={skeletonStyle("100%", "38px")}
          ></div>
        </Col>

        <Col>
          <Col>
            <div
              className="skeleton-loader"
              style={skeletonStyle("80px")}
            ></div>
          </Col>
          <Col className="mb-4 mt-2">
            <div
              className="skeleton-loader"
              style={skeletonStyle("40px", "24px")}
            ></div>
          </Col>
        </Col>
      </Row>

      <div className="mt-3">
        <div
          className="skeleton-loader"
          style={skeletonStyle("100%", "40px")}
        ></div>
      </div>

      <div className="mt-5">
        {/* Title field skeleton */}
        <Row className="mb-3">
          <Col>
            <div
              className="skeleton-loader"
              style={skeletonStyle("80px")}
            ></div>
            <div
              className="mt-2 skeleton-loader"
              style={skeletonStyle("100%", "38px")}
            ></div>
          </Col>
        </Row>

        {/* Content field skeleton */}
        <Row className="mb-3">
          <Col>
            <div
              className="skeleton-loader"
              style={skeletonStyle("80px")}
            ></div>
            <div
              className="mt-2 skeleton-loader"
              style={skeletonStyle("100%", "600px")}
            ></div>
          </Col>
        </Row>

        {/* Buttons skeleton */}
        <Row className="mt-4">
          <Col className="d-flex justify-content-end">
            <div
              className="skeleton-loader me-2"
              style={skeletonStyle("100px", "38px")}
            ></div>
            <div
              className="skeleton-loader"
              style={skeletonStyle("100px", "38px")}
            ></div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

const CreateCms = ({ cmsData, loading, details }) => {
  const {
    navigate,
    createCms,
    selectedClient,
    selectedPortal,
    setSelectedClient,
    setSelectedPortal,
    editCms,
    cmsPageId,
    setTemplate,
    galleryModal,
    setGalleryModal,
    getGalleryData,
    cmsKeys,
    selectedTab,
    setSelectedTab,
    languages,
    deleteCms,
  } = useCreateCms(cmsData);

  const { isHidden } = useCheckPermission();

  const [label, setLabel] = useState('')
  const [titleErr, setTitleErr] = useState('')


  return (
    <>
      <div className="section-header-wrap mt-n3 mx-n3">
        <Row className="align-items-center justify-content-between">
          {loading && !cmsData ? (
            <Col>
              <h2 className="title-text">
                <div
                  className="skeleton-loader"
                  style={{
                    width: "200px",
                    height: "32px",
                    backgroundColor: "#e9ecef",
                    borderRadius: "4px",
                    animation: "pulse 1.5s infinite ease-in-out",
                  }}
                ></div>
              </h2>
            </Col>
          ) : (
            <Col>
             <div className='backbtn'>
             <PageBackButton />
              <h2 className="title-text">
                {cmsData
                  ? `${!details ? "Edit" : ""} CMS: ${cmsData?.title?.EN}`
                  : "Create CMS"}
              </h2>
              </div>
            </Col>
          )}
          <Col xs={2} className="text-end">
            <NotificationIconContainer />
          </Col>
        </Row>
      </div>

      {!cmsData && loading ? (
        <CreateCmsSkeleton />
      ) : (
        <div className="FormsBg">
          <Formik
            initialValues={{
              title: cmsData ? cmsData?.title?.EN : "",
              slug: cmsData ? cmsData?.slug : "",
              content: cmsData ? cmsData?.content?.EN : "",
              category: cmsData ? cmsData?.category : 1,
              isActive: cmsData ? !!cmsData?.isActive : true,
              language: selectedTab,
            }}
            validationSchema={createCmsSchema}
            onSubmit={(formValues) => {
              !cmsData
                ? createCms({
                  cmsData: {
                    ...formValues,
                    tenantId: selectedPortal,
                    adminUserId: selectedClient,
                  },
                })
                : editCms({
                  cmsData: {
                    ...formValues,
                    tenantId: selectedPortal,
                    adminUserId: selectedClient,
                    cmsPageId: parseInt(cmsPageId),
                  },
                });
            }}
          >
            {({
              values,
              errors,
              handleChange,
              handleSubmit,
              handleBlur,
              setFieldValue,
            }) => (
              <Form>
                <Row className="mb-3 align-items-center">


                  <Col xs={6}>
                    <Col>
                      <BForm.Label>
                        Title <span className='text-danger'>* </span>
                      </BForm.Label>
                    </Col>

                    <Col>
                      <BForm.Control
                        type='text'
                        name='title'
                        disabled={details}
                        placeholder='Enter Title'
                        value={label}
                        onChange={(e) => {
                          setLabel(e.target.value)
                          handleChange(e)
                          setTitleErr('')
                        }}
                        onBlur={handleBlur}
                      />

                      {titleErr
                        ? (
                          <Row>
                            <span className='text-danger'>{titleErr}</span>
                          </Row>)
                        : (
                          <ErrorMessage
                            component='div'
                            name='title'
                            className='text-danger'
                          />)}

                    </Col>
                  </Col>










                  <Col xs={6}>
                    <Col>
                      <BForm.Label>
                        Slug <span className="text-danger">*</span>
                      </BForm.Label>
                    </Col>
                    <Col>
                      <BForm.Control
                        type="text"
                        name="slug"
                        placeholder="Enter Slug"
                        value={values.slug}
                        disabled={details}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />

                      <ErrorMessage
                        component="div"
                        name="slug"
                        className="text-danger"
                      />
                    </Col>
                  </Col>

                  <Col xs={6}>
                    <BForm.Label>
                      Category <span className="text-danger">*</span>
                    </BForm.Label>

                    <BForm.Select
                      type="text"
                      name="category"
                      style={{ maxWidth: "200px" }}
                      value={values.category}
                      disabled={details}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    >
                      {/* <option value={1}>Support Links</option>
                      <option value={2}>Other Links</option> */}
                       {categoryLinkOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                    </BForm.Select>
                  </Col>

                  <Col>
                    <Col>
                      <BForm.Label>
                        Status <span className="text-danger">*</span>
                      </BForm.Label>
                    </Col>
                    <Col className="mb-4">
                      <BForm.Check
                        type="switch"
                        name="isActive"
                        disabled={details}
                        defaultChecked={values.isActive}
                        value={values.isActive}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Col>
                  </Col>
                </Row>

                {/* <Col>
                  <Col xs={2}>
                    <BForm.Label>
                      Title <span className='text-danger'>* </span>
                    </BForm.Label>
                  </Col>

                  <Col xs={5}>
                    <BForm.Control
                      type='text'
                      name='title'
                      disabled={details}
                      placeholder='Enter Title'
                      value={label}
                      onChange={(e) => {
                        setLabel(e.target.value)
                        handleChange(e)
                        setTitleErr('')
                      }}
                      onBlur={handleBlur}
                    />

                    {titleErr
                      ? (
                        <Row>
                          <span className='text-danger'>{titleErr}</span>
                        </Row>)
                      : (
                        <ErrorMessage
                          component='div'
                          name='title'
                          className='text-danger'
                        />)}

                  </Col>
                </Col> */}


                {/* <Tabs
                  activeKey={selectedTab}
                  onSelect={(tab) => setSelectedTab(tab)}
                  className="nav-light mt-3"
                > */}
                {/* <Tab
                    eventKey="EN"
                    // title={
                    //   <Trigger message="English">
                    //     <span>EN</span>
                    //   </Trigger>
                    // }
                    mountOnEnter
                    tabClassName={
                      selectedTab !== "EN" ? "email" : "email-active"
                    }
                  > */}


                <div className="mt-3">
                  <EditCMSTemplate
                    values={cmsData}
                    cmsKeys={cmsKeys}
                    setFieldValue={setFieldValue}
                    isHidden={isHidden}
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    setTemp={setTemplate}
                    handleSubmit={handleSubmit}
                    navigate={navigate}
                    deleteCms={deleteCms}
                    details={details}
                    initValues={values}
                    errors={errors}
                    titleErr={titleErr} setTitleErr={setTitleErr}
                    label={label} setLabel={setLabel}
                  />
                </div>
                {/* </Tab> */}
                {/* </Tabs> */}
              </Form>
            )}
          </Formik>
        </div>
      )}
      {galleryModal && (
        <GalleryModal
          galleryModal={galleryModal}
          setGalleryModal={setGalleryModal}
        />
      )}
    </>
  );
};

export default CreateCms;
